package com.kaihuoy.home.screens

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.kaihuoy.shared.data.InductionRecord
import com.kaihuoy.shared.data.InspectionRecord
import com.kaihuoy.shared.data.ServiceRecord
import com.kaihuoy.shared.data.User
import com.kaihuoy.web.FirebaseService
import com.kaihuoy.components.common.*
import com.kaihuoy.components.common.*

private val KIRJAUKSET_TABS = listOf("Perehdytykset", "Tarkastukset", "Huollot")

@Composable
fun OmatKirjauksetScreen(user: User?) {
    var activeTab by remember { mutableStateOf("Perehdytykset") }
    var inductions by remember { mutableStateOf<List<InductionRecord>>(emptyList()) }
    var inspections by remember { mutableStateOf<List<InspectionRecord>>(emptyList()) }
    var services by remember { mutableStateOf<List<ServiceRecord>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }

    LaunchedEffect(user?.uid) {
        if (user == null) { isLoading = false; return@LaunchedEffect }
        isLoading = true
        try {
            launch {
                FirebaseService.database.reference("inductionRecords").valueEvents.collect { s ->
                    inductions = s.children.mapNotNull { child ->
                        try {
                            val r = child.value<InductionRecord>()
                            if (r.userId == user.uid) (if (r.id.isBlank()) r.copy(id = child.key ?: "") else r) else null
                        } catch (_: Exception) { null }
                    }.sortedByDescending { it.timestamp }
                    isLoading = false
                }
            }
            launch {
                FirebaseService.database.reference("inspectionRecords").valueEvents.collect { s ->
                    inspections = s.children.mapNotNull { child ->
                        try {
                            val r = child.value<InspectionRecord>()
                            if (r.userId == user.uid) (if (r.id.isBlank()) r.copy(id = child.key ?: "") else r) else null
                        } catch (_: Exception) { null }
                    }.sortedByDescending { it.timestamp }
                }
            }
            launch {
                FirebaseService.database.reference("serviceRecords").valueEvents.collect { s ->
                    services = s.children.mapNotNull { child ->
                        try {
                            val r = child.value<ServiceRecord>()
                            if (r.userId == user.uid) (if (r.id.isBlank()) r.copy(id = child.key ?: "") else r) else null
                        } catch (_: Exception) { null }
                    }.sortedByDescending { it.serviceDate }
                }
            }
        } catch (_: kotlinx.coroutines.CancellationException) {
        } catch (_: Exception) { isLoading = false }
    }

    Div(attrs = { style { padding(20.px); maxWidth(960.px); property("margin", "0 auto") } }) {
        H1(attrs = { style { margin(0.px, 0.px, 16.px, 0.px); color(KaihuTheme.TextPrimary); fontSize(1.8.em); fontWeight("800") } }) {
            Text("Omat kirjaukset")
        }

        Div(attrs = {
            style {
                display(DisplayStyle.Flex); gap(4.px); marginBottom(20.px)
                backgroundColor(Color.white); borderRadius(12.px); padding(4.px)
                property("border", "1px solid #E9ECEF")
            }
        }) {
            KIRJAUKSET_TABS.forEach { tab ->
                val count = when (tab) {
                    "Perehdytykset" -> inductions.size
                    "Tarkastukset" -> inspections.size
                    else -> services.size
                }
                KirjauksetTab(tab, count, tab == activeTab) { activeTab = tab }
            }
        }

        when {
            isLoading -> Div(attrs = { style { padding(40.px); textAlign("center"); color(KaihuTheme.TextSecondary) } }) {
                Text("Ladataan kirjauksia...")
            }
            activeTab == "Perehdytykset" -> KirjauksetList(
                items = inductions,
                emptyText = "Ei perehdytyskirjauksia",
                emptyIcon = "✅"
            ) { record ->
                KirjausRow(
                    title = "Perehdytys",
                    subtitle = if (record.siteName.isNotBlank()) record.siteName else record.employer,
                    timestamp = record.timestamp,
                    status = record.status
                )
            }
            activeTab == "Tarkastukset" -> KirjauksetList(
                items = inspections,
                emptyText = "Ei tarkastuskirjauksia",
                emptyIcon = "🛠️"
            ) { record ->
                KirjausRow(
                    title = record.machineType.ifBlank { "Tarkastus" },
                    subtitle = record.siteName,
                    timestamp = record.timestamp,
                    status = "completed"
                )
            }
            else -> KirjauksetList(
                items = services,
                emptyText = "Ei huoltokirjauksia",
                emptyIcon = "🔧"
            ) { record ->
                KirjausRow(
                    title = record.serviceType.ifBlank { "Huolto" },
                    subtitle = record.equipmentId,
                    timestamp = record.serviceDate,
                    status = "completed"
                )
            }
        }
    }
}

@Composable
private fun <T> KirjauksetList(
    items: List<T>,
    emptyText: String,
    emptyIcon: String,
    rowContent: @Composable (T) -> Unit
) {
    if (items.isEmpty()) {
        Div(attrs = {
            style {
                backgroundColor(Color.white); borderRadius(16.px); padding(48.px, 24.px)
                property("border", "1px solid #E9ECEF"); textAlign("center"); color(KaihuTheme.TextSecondary)
            }
        }) {
            Div(attrs = { style { fontSize(2.5.em); marginBottom(12.px) } }) { Text(emptyIcon) }
            Div { Text(emptyText) }
        }
    } else {
        Div(attrs = {
            style {
                backgroundColor(Color.white); borderRadius(16.px)
                property("border", "1px solid #E9ECEF"); property("overflow", "hidden")
            }
        }) {
            items.forEach { item -> rowContent(item) }
        }
    }
}

@Composable
private fun KirjausRow(title: String, subtitle: String, timestamp: Long, status: String) {
    val dateStr = if (timestamp > 0) {
        val d = kotlin.js.Date(timestamp.toDouble())
        "${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}"
    } else "-"

    val (statusBg, statusFg, statusLabel) = when (status) {
        "completed" -> Triple("#D8F3DC", "#1B4332", "Valmis")
        "draft"     -> Triple("#FFF3CD", "#856404", "Luonnos")
        else        -> Triple("#F1F3F5", "#6C757D", status)
    }

    Div(attrs = {
        style {
            display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.SpaceBetween)
            padding(14.px, 18.px); gap(12.px)
            property("border-bottom", "1px solid #F1F3F5")
        }
    }) {
        Div {
            Div(attrs = { style { fontWeight("600"); color(KaihuTheme.TextPrimary); marginBottom(2.px) } }) { Text(title) }
            if (subtitle.isNotBlank()) Div(attrs = { style { fontSize(0.85.em); color(KaihuTheme.TextSecondary) } }) { Text(subtitle) }
        }
        Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(10.px) } }) {
            Span(attrs = { style { fontSize(0.82.em); color(KaihuTheme.TextLight) } }) { Text(dateStr) }
            Span(attrs = {
                style {
                    backgroundColor(Color(statusBg)); color(Color(statusFg))
                    padding(3.px, 10.px); borderRadius(20.px); fontSize(0.75.em); fontWeight("700")
                }
            }) { Text(statusLabel) }
        }
    }
}

@Composable
private fun KirjauksetTab(label: String, count: Int, isActive: Boolean, onClick: () -> Unit) {
    Div(attrs = {
        onClick { onClick() }
        style {
            flex(1); padding(10.px); borderRadius(10.px); textAlign("center"); cursor("pointer")
            fontWeight(if (isActive) "700" else "500"); fontSize(0.9.em)
            backgroundColor(if (isActive) KaihuTheme.Primary else Color.transparent)
            color(if (isActive) Color.white else KaihuTheme.TextSecondary)
            property("transition", "all 0.15s"); property("user-select", "none")
        }
    }) {
        Text(label)
        if (count > 0) Span(attrs = {
            style {
                marginLeft(6.px)
                backgroundColor(if (isActive) rgba(255, 255, 255, 0.25) else Color("#E9ECEF"))
                color(if (isActive) Color.white else KaihuTheme.TextSecondary)
                padding(1.px, 7.px); borderRadius(20.px); fontSize(0.82.em); fontWeight("700")
            }
        }) { Text(count.toString()) }
    }
}
